'use client'

import { memo } from 'react';
import { Link } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import ICasino from '../../models/ICasino'
import { Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import CasinoRating from './CasinoRating';
import CasinoMinBetsInSearch from './CasinoMinBetsInSearch';


/**
 * Deterministic index into the theme's casino accent palette derived from the
 * casino name, so each row keeps a stable splash of identity without any casino
 * branding.
 */
function accentIndex(seed: string, count: number): number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % count;
}


/**
 * This is a React functional component that renders the casino data in the list of casinos.
 * @returns {JSX.Element}
 */
function CasinoListItem({ casino }: { casino: ICasino }) {
    const { casinoAccents } = useTheme();
    const accent = casinoAccents[accentIndex(casino.name, casinoAccents.length)];
    return (
        <Link to={`/casinos/${casino.id}-${casino.nameIdentifier}`} style={{ textDecoration: 'none', color: 'inherit' }}>
            <ListItem sx={{ px: { xs: 1, sm: 2 } }}>
                <Box
                    aria-hidden
                    sx={{
                        width: 4,
                        height: { xs: 88, md: 96 },
                        borderRadius: 2,
                        backgroundColor: accent,
                        mr: { xs: 1.5, sm: 2.5 },
                        flexShrink: 0,
                    }}
                />
                <ListItemText sx={{ my: 0, height: 140 }}>
                    <Grid sx={{ alignItems: 'center', height: 120 }} container>
                        <Grid container size={4}>
                            <Grid size={12}>
                                <Typography sx={{ display: { xs: 'block', md: 'none' }, fontWeight: 700 }} variant='h6'>{casino.shortName}</Typography>
                                <Typography sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 700 }} variant='h5'>{casino.name}</Typography>
                            </Grid>
                            <Grid size={12}>
                                <CasinoRating rating={casino.rating} userRatings={casino.userRatings} />
                            </Grid>
                        </Grid>
                        <CasinoMinBetsInSearch casino={casino} />
                    </Grid>
                    <Divider style={{ marginTop: '5px' }} />
                </ListItemText>
            </ListItem>
        </Link>
    );
}

export default memo(CasinoListItem);
